import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputTextarea} from "primereact/inputtextarea";
import {Toast} from "primereact/toast";
import {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {useAccessLostByReassignmentHandler} from "../hooks/accessLostByReassignmentHooks";
import {selectRevaId} from "../redux/persistenceSlice";
import {assignValuation, reopenPersistence} from "../redux/persistenceThunk";
import {CorrectionOwnerSelect} from "./CorrectionOwnerSelect";

/**
 * Dialog that reopens a finalized valuation for correction. Requires a (non-empty) comment and lets the user
 * choose the new owner: the order pool (default) or — only with reva-ownership-assign — a finalize-capable
 * person (see {@link CorrectionOwnerSelect}). Dispatches {@link reopenPersistence} (which releases the owner to
 * the pool) and, if a person was chosen, {@link assignValuation} afterwards. Reports the result via toast; if the
 * assignment cost the user their access (backend notification accessLostByReassignment), it then returns to the
 * start page (#19302 Z-B1).
 *
 * Controlled via `visible` / `onHide`; manages its own comment/owner state.
 * @param {{visible: boolean, onHide: () => void}} props
 * @return {React.JSX.Element}
 */
export default function ReopenForCorrectionDialog({visible, onHide}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const toast = useRef(null);
    const revaId = useSelector(selectRevaId);
    const [comment, setComment] = useState("");
    // null = Auftragspool (Standard); ein Username = direkte Zuweisung an diese Person.
    const [owner, setOwner] = useState(null);
    const [busy, setBusy] = useState(false);
    const handleAccessLost = useAccessLostByReassignmentHandler();

    const close = () => {
        setComment("");
        setOwner(null);
        onHide();
    };

    const reopen = async () => {
        setBusy(true);
        const reopened = await dispatch(reopenPersistence(comment));
        // reopenPersistence gibt den Owner in den Pool frei; nur bei expliziter Personenwahl noch zuweisen.
        let assignFailed = false;
        let assignResponse = null;
        if (reopened && owner) {
            try {
                assignResponse = await dispatch(assignValuation(revaId, owner));
            } catch (err) {
                console.error("Error assigning owner after reopen", err);
                assignFailed = true;
            }
        }
        setBusy(false);
        close();
        if (!reopened) {
            toast.current?.show({
                severity: "error",
                summary: "Fehler",
                detail: t("object.error.reopening"),
                life: 5000,
            });
        } else if (assignFailed) {
            toast.current?.show({
                severity: "warn",
                summary: "Warn",
                detail: t("value.correction.owner.assignFailed"),
                life: 6000,
            });
        } else {
            toast.current?.show({
                severity: "success",
                summary: t("value.actions.reopenHandoffToastSummary"),
                detail: owner ? t("value.correction.owner.assignedToast") : t("value.actions.reopenHandoffToastDetail"),
                life: 6000,
            });
            handleAccessLost(assignResponse, toast.current);
        }
    };

    const footer = (
        <div>
            <Button
                className="p-button-text"
                label={t("general.cancel")}
                icon="pi pi-times"
                tabIndex={3}
                onClick={close}
                disabled={busy}
            />
            <Button
                className="ml-2"
                label={t("value.actions.reopen")}
                icon="pi pi-check"
                tabIndex={2}
                autoFocus
                disabled={busy || !comment || comment.trim().length === 0}
                onClick={reopen}
            />
        </div>
    );

    return (
        <>
            <Dialog
                visible={visible}
                onHide={close}
                header={t("object.reopenComment")}
                footer={footer}
                style={{width: "50vw"}}
                closeOnEscape={!busy}
                pt={{
                    closeButton: {className: "hidden"},
                    content: {className: "p-6 pt-0"},
                    headerTitle: {className: "font-normal"},
                }}
            >
                <InputTextarea
                    id="reopenCommentInput"
                    className="w-full"
                    value={comment}
                    rows={5}
                    tabIndex={1}
                    onChange={(e) => setComment(e.target.value || "")}
                    disabled={busy}
                />
                <CorrectionOwnerSelect revaId={revaId} value={owner} onChange={(o) => setOwner(o)} disabled={busy} />
            </Dialog>
            <Toast ref={toast} />
        </>
    );
}
